// Phase 8 (Comitê & IA v2) — FILA DE SUBSTITUIÇÃO (R3.2-bis; ADR-0013). Visão
// ESTREITA, content-free e escopada-a-conflito: lista só os casos que precisam
// recompor (um membro do roster que se RECUSOU e ainda não foi substituído). NÃO
// é browser de casos — nenhuma superfície de conteúdo do caso (ADR-0001/0002).
// Fluxo: fila → caso → impedido (do roster) → substituto (do pool) → POST substituto.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Field } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';

interface FilaItem {
  caseId: string;
  stateKind: string;
  roster: string[];
  impedidos: string[];
}
interface PoolUser {
  id: string;
  email: string;
  roles: string[];
  subRole?: string;
  active: boolean;
}

export function SubstituicoesPage(): JSX.Element {
  const { t } = useTranslation();
  const [fila, setFila] = useState<FilaItem[]>([]);
  const [pool, setPool] = useState<PoolUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [off, setOff] = useState(false);
  const [busy, setBusy] = useState<string | null>(null); // `${caseId}::${impedido}`
  // Substituto escolhido por impedido: chave `${caseId}::${impedido}` → paraMemberId.
  const [escolha, setEscolha] = useState<Record<string, string>>({});

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    const [qRes, uRes] = await Promise.all([
      apiFetch('/api/admin/substituicoes-pendentes'),
      apiFetch('/api/admin/users'),
    ]);
    if (qRes.status === 404) {
      setOff(true); // OFF-safe: rito v2 desligado no worker.
      return;
    }
    if (!qRes.ok) {
      setError('load');
      return;
    }
    const qJson = (await qRes.json()) as { substituicoes: FilaItem[] };
    setFila(qJson.substituicoes);
    if (uRes.ok) {
      const uJson = (await uRes.json()) as { users: PoolUser[] };
      setPool(uJson.users.filter((u) => u.active && u.roles.includes('comite_membro')));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function designar(caseId: string, deMemberId: string): Promise<void> {
    const key = `${caseId}::${deMemberId}`;
    const paraMemberId = escolha[key];
    if (!paraMemberId) return;
    setBusy(key);
    setError(null);
    try {
      const res = await apiFetch(`/api/admin/cases/${caseId}/substituto`, {
        method: 'POST',
        body: JSON.stringify({ deMemberId, paraMemberId }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        setError(j.error ?? 'substituto');
        return;
      }
      await reload();
    } finally {
      setBusy(null);
    }
  }

  if (off) {
    return (
      <section>
        <h1>{t('substituicoes.title')}</h1>
        <p role="status">{t('substituicoes.off')}</p>
      </section>
    );
  }

  return (
    <section>
      <h1>{t('substituicoes.title')}</h1>
      <p>{t('substituicoes.intro')}</p>
      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {t('substituicoes.erro', { error })}
        </Alert>
      )}
      {fila.length === 0 ? (
        <p role="status">{t('substituicoes.empty')}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 'var(--space-4)' }}>
          {fila.map((item) => (
            <li key={item.caseId}>
              <Card>
                <h2 style={{ marginTop: 0 }}>
                  {t('substituicoes.caso_label')}: <code>{item.caseId}</code>
                </h2>
                <p>
                  {t('substituicoes.estado_label')}: {item.stateKind}
                </p>
                <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                  {item.impedidos.map((imp) => {
                    const key = `${item.caseId}::${imp}`;
                    // Pool elegível: membros ativos fora do roster atual do caso.
                    const candidatos = pool.filter((u) => !item.roster.includes(u.id));
                    return (
                      <Card
                        key={key}
                        padding="sm"
                        role="group"
                        aria-label={t('substituicoes.impedido_label')}
                      >
                        <p style={{ marginTop: 0 }}>
                          {t('substituicoes.impedido_label')}: <code>{imp}</code>
                        </p>
                        <div
                          style={{
                            display: 'flex',
                            gap: 'var(--space-3)',
                            alignItems: 'flex-end',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Field label={t('substituicoes.substituto_label')}>
                            {({ id }) => (
                              <select
                                id={id}
                                className="cf-input"
                                value={escolha[key] ?? ''}
                                onChange={(e) =>
                                  setEscolha((s) => ({ ...s, [key]: e.target.value }))
                                }
                              >
                                <option value="">—</option>
                                {candidatos.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.email} ({u.subRole ?? '—'})
                                  </option>
                                ))}
                              </select>
                            )}
                          </Field>
                          <Button
                            disabled={busy === key || !escolha[key]}
                            loading={busy === key}
                            onClick={() => void designar(item.caseId, imp)}
                          >
                            {busy === key
                              ? t('substituicoes.designando')
                              : t('substituicoes.designar')}
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
